import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { X, Printer, FileText, Package, MapPin, Globe, DollarSign, Table } from 'lucide-react';

const PRINT_BASE_CSS = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
        direction: rtl;
        background: white;
    }
    @media print {
        body { padding: 0 !important; }
        .no-print { display: none; }
    }
    table { border-collapse: collapse; width: 100%; margin: 20px 0; }
    th, td { padding: 8px; border: 1px solid #ddd; }
    th { background-color: #f5f5f5; font-weight: bold; }
    .text-center { text-align: center; }
    .text-left { text-align: left; }
    .text-right { text-align: right; }
    .border { border: 1px solid #ddd; }
    .rounded-lg { border-radius: 8px; }
    .p-4 { padding: 16px; }
    .mb-3, .mb-2, .mb-1 { margin-bottom: 12px; }
    .mt-3 { margin-top: 12px; }
    .font-bold { font-weight: bold; }
    .font-semibold { font-weight: 600; }
    .text-lg { font-size: 1.125rem; }
    .text-sm { font-size: 0.875rem; }
    .text-xs { font-size: 0.75rem; }
    .space-y-4 > * + * { margin-top: 16px; }
    .space-y-6 > * + * { margin-top: 24px; }
    .grid { display: grid; }
    .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
    .gap-4 { gap: 16px; }
    .border-b { border-bottom: 1px solid #ddd; }
    .border-b-2 { border-bottom: 2px solid #ddd; }
    .pb-4 { padding-bottom: 16px; }
    .bg-warning-50 { background-color: #fefce8; }
    .border-warning-100 { border-color: #fef08a; }
    .bg-mekomit-50 { background-color: #f0fdf4; }
    .break-inside-avoid { break-inside: avoid; }
`;

// Brand colors (from tailwind.config.js). Only applied when the user opts into
// color printing — otherwise the document stays black-on-white. `print-color-adjust:
// exact` forces the browser to actually render background fills when printing.
// In color mode NOTHING is black or gray: every ink/neutral tone is mapped onto
// the olive brand palette, so the whole document prints in shades of green.
const PRINT_COLOR_CSS = `
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    /* Default text + any unclassed element → clearly olive green (never black) */
    body { color: #586543; }
    /* Brand (mekomit) text — kept vivid, away from near-black */
    .text-mekomit-600 { color: #6f7e54; }
    .text-mekomit-700 { color: #586543; }
    .text-mekomit-800 { color: #586543; }
    .text-mekomit-900 { color: #586543; }
    /* Ink text — remapped off black/gray onto visibly green brand tones */
    .text-ink { color: #586543; }
    .text-ink-strong { color: #586543; }
    .text-ink-muted { color: #6f7e54; }
    .text-ink-subtle { color: #8B9A6B; }
    /* Backgrounds */
    .bg-mekomit-50 { background-color: #f5f7ef; }
    .bg-mekomit-100 { background-color: #e9eedc; }
    .bg-warning-50 { background-color: #fdf6e7; }
    /* Borders — base CSS uses gray (#ddd); recolor every border to brand olive */
    table, th, td, .border, .border-b, .border-b-2 { border-color: #d2dcb9 !important; }
    th { background-color: #f5f7ef !important; color: #586543; }
    .border-mekomit-200 { border-color: #d2dcb9 !important; }
    .border-warning-100 { border-color: #fae7c0 !important; }
`;

// Format an order's selected time slot (pickup / delivery window) for display.
// Returns '' when the order was placed without a time slot.
const formatTimeSlot = (order) => {
    if (!order?.time_slot_date || !order?.delivery_time) return '';
    const dateStr = new Date(order.time_slot_date).toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric', year: 'numeric' });
    return `${dateStr} ${order.delivery_time}`;
};

/**
 * PrintOrdersModal Component
 * Provides various print formats for orders with A4-friendly layouts
 */
export const PrintOrdersModal = ({ orders, deliveryPoints = [], deliveryAreas = [], products = [], categories = [], onClose }) => {
    const [selectedFormat, setSelectedFormat] = useState('summary');
    const [fontScale, setFontScale] = useState(1);
    const [orientation, setOrientation] = useState('portrait');
    const [marginMm, setMarginMm] = useState(10);
    const [colorMode, setColorMode] = useState('bw'); // 'color' | 'bw'
    // Tagged measurement: only trust if it's for the current srcDoc
    const [measurement, setMeasurement] = useState({ srcDoc: '', height: 0 });

    // A4 dimensions in CSS pixels (96 DPI)
    const MM_PX = 96 / 25.4;
    const pageWidthPx = Math.round((orientation === 'portrait' ? 210 : 297) * MM_PX);
    const pageHeightPx = Math.round((orientation === 'portrait' ? 297 : 210) * MM_PX);

    // Print formats configuration
    const formats = [
        { id: 'summary', label: 'סיכום הזמנות', description: 'הזמנות מפורטות עם כל הנתונים', icon: FileText },
        { id: 'summary-table', label: 'סיכום הזמנות (טבלה)', description: 'טבלה מרוכזת - הרבה הזמנות בעמוד אחד', icon: Table },
        { id: 'by-product', label: 'סיכום לפי מוצר', description: 'כמויות ומספר הזמנות לכל מוצר', icon: Package },
        { id: 'by-product-by-area', label: 'סיכום מוצרים לפי אזורים', description: 'כמויות לכל מוצר בכל אזור / נקודת חלוקה', icon: Package },
        { id: 'by-delivery-point', label: 'סיכום לפי נקודת חלוקה', description: 'קיבוץ לפי נקודות חלוקה', icon: MapPin },
        { id: 'by-delivery-area', label: 'סיכום לפי אזור משלוח', description: 'קיבוץ לפי יישובים ואזורים', icon: Globe },
        { id: 'revenues', label: 'סיכום הכנסות', description: 'סה״כ הכנסות ופירוט תשלומים', icon: DollarSign }
    ];

    const iframeRef = useRef(null);

    // Render the selected format component
    const renderFormat = () => {
        switch (selectedFormat) {
            case 'summary':
                return <OrdersSummaryFormat orders={orders} />;
            case 'summary-table':
                return <OrdersSummaryTableFormat orders={orders} />;
            case 'by-product':
                return <ByProductFormat orders={orders} products={products} categories={categories} />;
            case 'by-product-by-area':
                return <ByProductByAreaFormat orders={orders} />;
            case 'by-delivery-point':
                return <ByDeliveryPointFormat orders={orders} deliveryPoints={deliveryPoints} />;
            case 'by-delivery-area':
                return <ByDeliveryAreaFormat orders={orders} deliveryAreas={deliveryAreas} />;
            case 'revenues':
                return <RevenuesFormat orders={orders} />;
            default:
                return null;
        }
    };

    // Self-contained HTML document for the iframe preview / print — the
    // content flows continuously inside, browser paginates only on print.
    const srcDoc = useMemo(() => {
        const htmlContent = renderToStaticMarkup(renderFormat());
        return `<!DOCTYPE html>
<html dir="rtl">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>הדפסת הזמנות - Mekomit</title>
    <style>
        @page { size: A4 ${orientation}; margin: ${marginMm}mm; }
        html { font-size: ${Math.round(fontScale * 16)}px; }
        body { padding: ${marginMm}mm; }
        ${PRINT_BASE_CSS}
        ${colorMode === 'color' ? PRINT_COLOR_CSS : ''}
    </style>
</head>
<body>${htmlContent}</body>
</html>`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFormat, fontScale, orientation, marginMm, colorMode, orders, products, categories, deliveryPoints, deliveryAreas]);

    const handleIframeLoad = useCallback(() => {
        const iframe = iframeRef.current;
        const doc = iframe?.contentDocument;
        if (!doc?.body) return;
        requestAnimationFrame(() => {
            const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
            setMeasurement({ srcDoc, height: h });
        });
    }, [srcDoc]);

    const contentTotalHeightPx = (measurement.srcDoc === srcDoc && measurement.height) || pageHeightPx;

    // Auto-fit preview to available pane width
    const previewPaneRef = useRef(null);
    const [paneSize, setPaneSize] = useState({ width: 0, height: 0 });
    useEffect(() => {
        const el = previewPaneRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(entries => {
            for (const e of entries) setPaneSize({ width: e.contentRect.width, height: e.contentRect.height });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const availableWidth = Math.max(0, paneSize.width - 32);
    const fitScale = pageWidthPx > 0 && availableWidth > 0
        ? availableWidth / pageWidthPx
        : 1;

    const handlePrint = () => {
        const iframe = iframeRef.current;
        if (!iframe || !iframe.contentWindow) return;
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
    };

    return (
        <>
            {/* Screen-only Modal - Hidden when printing */}
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4 print:hidden">
                <div className="bg-white rounded-2xl w-full max-w-7xl shadow-elevated max-h-[95vh] flex flex-col" dir="rtl">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-line-subtle bg-white rounded-t-2xl">
                        <div className="flex items-center gap-3">
                            <Printer className="w-6 h-6 text-mekomit-600" />
                            <h2 className="text-2xl font-bold text-mekomit-900">הדפסת הזמנות</h2>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-surface-muted rounded-lg transition">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Body: Settings + Live preview */}
                    <div className="flex-1 overflow-y-auto md:overflow-hidden flex flex-col md:flex-row min-h-0">
                        {/* Settings panel */}
                        <aside className="md:w-80 lg:w-96 md:shrink-0 md:overflow-y-auto border-b md:border-b-0 md:border-l border-line-subtle p-5 space-y-5">
                            <div>
                                <h3 className="text-sm font-semibold text-ink mb-2">פורמט הדפסה</h3>
                                <div className="space-y-1.5">
                                    {formats.map(format => {
                                        const Icon = format.icon;
                                        const isSelected = selectedFormat === format.id;
                                        return (
                                            <button
                                                key={format.id}
                                                type="button"
                                                onClick={() => setSelectedFormat(format.id)}
                                                className={`w-full p-2.5 rounded-lg border-2 transition-all text-right ${
                                                    isSelected
                                                        ? 'border-mekomit-500 bg-mekomit-50'
                                                        : 'border-line bg-white hover:border-mekomit-300 hover:bg-mekomit-50/30'
                                                }`}
                                            >
                                                <div className="flex items-start gap-2">
                                                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${isSelected ? 'text-mekomit-600' : 'text-ink-subtle'}`} />
                                                    <div className="flex-1 min-w-0">
                                                        <div className={`text-xs font-semibold leading-tight ${isSelected ? 'text-mekomit-900' : 'text-ink-strong'}`}>
                                                            {format.label}
                                                        </div>
                                                        <div className="text-[11px] text-ink-muted leading-snug mt-0.5">{format.description}</div>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="pt-4 border-t border-line-subtle space-y-3">
                                <h3 className="text-sm font-semibold text-ink">הגדרות הדפסה</h3>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-ink-strong">גודל גופן</span>
                                    <div className="flex items-center border border-line rounded-lg overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => setFontScale(s => Math.max(0.6, +(s - 0.1).toFixed(2)))}
                                            disabled={fontScale <= 0.6}
                                            className="px-2.5 py-1 text-sm font-bold hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed transition"
                                            aria-label="הקטן גודל גופן"
                                        >−</button>
                                        <span className="px-2 py-1 text-xs font-medium tabular-nums w-14 text-center border-x border-line">
                                            {Math.round(fontScale * 100)}%
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setFontScale(s => Math.min(1.6, +(s + 0.1).toFixed(2)))}
                                            disabled={fontScale >= 1.6}
                                            className="px-2.5 py-1 text-sm font-bold hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed transition"
                                            aria-label="הגדל גודל גופן"
                                        >+</button>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-ink-strong">כיוון הדף</span>
                                    <div className="flex border border-line rounded-lg overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => setOrientation('portrait')}
                                            className={`px-3 py-1 text-xs font-medium transition ${orientation === 'portrait' ? 'bg-mekomit-500 text-white' : 'hover:bg-surface-muted text-ink-strong'}`}
                                        >לאורך</button>
                                        <button
                                            type="button"
                                            onClick={() => setOrientation('landscape')}
                                            className={`px-3 py-1 text-xs font-medium transition ${orientation === 'landscape' ? 'bg-mekomit-500 text-white' : 'hover:bg-surface-muted text-ink-strong'}`}
                                        >לרוחב</button>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-ink-strong">שוליים</span>
                                    <div className="flex items-center border border-line rounded-lg overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => setMarginMm(m => Math.max(0, m - 2))}
                                            disabled={marginMm <= 0}
                                            className="px-2.5 py-1 text-sm font-bold hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed transition"
                                            aria-label="הקטן שוליים"
                                        >−</button>
                                        <span className="px-2 py-1 text-xs font-medium tabular-nums w-14 text-center border-x border-line">
                                            {marginMm} מ״מ
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setMarginMm(m => Math.min(30, m + 2))}
                                            disabled={marginMm >= 30}
                                            className="px-2.5 py-1 text-sm font-bold hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed transition"
                                            aria-label="הגדל שוליים"
                                        >+</button>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-ink-strong">צבע</span>
                                    <div className="flex border border-line rounded-lg overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => setColorMode('color')}
                                            className={`px-3 py-1 text-xs font-medium transition ${colorMode === 'color' ? 'bg-mekomit-500 text-white' : 'hover:bg-surface-muted text-ink-strong'}`}
                                        >צבעוני</button>
                                        <button
                                            type="button"
                                            onClick={() => setColorMode('bw')}
                                            className={`px-3 py-1 text-xs font-medium transition ${colorMode === 'bw' ? 'bg-mekomit-500 text-white' : 'hover:bg-surface-muted text-ink-strong'}`}
                                        >שחור־לבן</button>
                                    </div>
                                </div>
                            </div>
                        </aside>

                        {/* Live preview - one continuous A4-wide page */}
                        <main ref={previewPaneRef} className="flex-1 md:overflow-auto bg-surface-muted p-4 md:min-h-0">
                            <div
                                className="mx-auto bg-white shadow-elevated"
                                style={{
                                    width: pageWidthPx * fitScale + 'px',
                                    height: contentTotalHeightPx * fitScale + 'px',
                                    overflow: 'hidden',
                                }}
                            >
                                <iframe
                                    ref={iframeRef}
                                    srcDoc={srcDoc}
                                    onLoad={handleIframeLoad}
                                    scrolling="no"
                                    title="תצוגה מקדימה להדפסה"
                                    style={{
                                        width: pageWidthPx + 'px',
                                        height: contentTotalHeightPx + 'px',
                                        border: 0,
                                        display: 'block',
                                        transform: `scale(${fitScale})`,
                                        transformOrigin: 'top right',
                                    }}
                                />
                            </div>
                        </main>
                    </div>

                    {/* Actions - Fixed at bottom */}
                    <div className="border-t border-line-subtle p-6 bg-white rounded-b-2xl">
                        <div className="flex gap-3 justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2.5 rounded-lg border border-line-strong hover:bg-surface-muted transition text-ink-strong font-medium"
                            >
                                ביטול
                            </button>
                            <button
                                type="button"
                                onClick={handlePrint}
                                className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-white hover:from-mekomit-600 hover:to-mekomit-500 transition font-semibold flex items-center gap-2"
                            >
                                <Printer className="w-4 h-4" />
                                הדפס
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

/**
 * Orders Summary Format - Full details of all orders
 */
const OrdersSummaryFormat = ({ orders }) => {
    return (
        <div className="space-y-6 print:space-y-0">
            <div className="text-center mb-8 pb-4 border-b-2 border-mekomit-200 print:hidden">
                <h1 className="text-3xl font-bold text-mekomit-900 mb-2">סיכום הזמנות</h1>
                <p className="text-sm text-ink-muted">
                    {new Date().toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-sm text-ink-muted">סה״כ {orders.length} הזמנות</p>
            </div>

            <div className="space-y-4 print:space-y-3">
                {orders.map((order, idx) => {
                    const customerName = order.customer?.name || order.customer_name || 'לא צוין';
                    const customerPhone = order.customer?.phone || order.customer_phone || '';
                    const customerEmail = order.customer?.email || order.customer_email || '';
                    const totalPrice = order.total || order.total_price || '0';

                    return (
                        <div key={order.order_id} className="border border-line rounded-lg p-4 print:p-3 print:mb-3 break-inside-avoid">
                            <div className="flex justify-between items-start mb-3 pb-3 border-b border-line-subtle">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-lg font-bold text-ink-strong">הזמנה #{order.order_id}</span>
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-mekomit-100 text-mekomit-800 font-semibold">
                                            {order.status}
                                        </span>
                                    </div>
                                    <div className="text-sm text-ink-muted">
                                        {new Date(order.created_at).toLocaleDateString('he-IL')} · {new Date(order.created_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                                <div className="text-left">
                                    <div className="text-2xl font-bold text-mekomit-600">₪{totalPrice}</div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-3">
                                <div>
                                    <h4 className="text-xs font-bold text-ink mb-1">פרטי לקוח</h4>
                                    <div className="text-sm space-y-0.5">
                                        <div><span className="font-semibold">{customerName}</span></div>
                                        {customerPhone && <div className="text-ink-muted">{customerPhone}</div>}
                                        {customerEmail && <div className="text-ink-muted truncate">{customerEmail}</div>}
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-xs font-bold text-ink mb-1">משלוח</h4>
                                    <div className="text-sm space-y-0.5">
                                        {(order.fulfillment_type === 'takeaway' || order.fulfillment_type === 'eat_in') ? (
                                            <div className="font-medium text-ink-strong">
                                                {order.delivery_location_name || (order.fulfillment_type === 'takeaway' ? 'איסוף עצמי' : 'שירות במקום')} {order.fulfillment_type === 'takeaway' ? '🛍️' : '🏪'}
                                            </div>
                                        ) : order.fulfillment_type === 'delivery_area' ? (
                                            <div className="text-ink-strong">
                                                <div className="font-medium">{order.delivery_location_name || order.delivery_area_name}</div>
                                                {(order.customer_address || order.customer?.address) && (
                                                    <div className="text-xs text-ink-muted">{order.customer_address || order.customer?.address}</div>
                                                )}
                                            </div>
                                        ) : (
                                            order.delivery_point_name && (
                                                <div className="font-medium text-ink-strong">{order.delivery_point_name}</div>
                                            )
                                        )}
                                        {formatTimeSlot(order) && (
                                            <div className="text-ink-muted">זמן איסוף: {formatTimeSlot(order)}</div>
                                        )}
                                        {order.payment_method && (
                                            <div className="text-ink-muted">תשלום: {order.payment_method}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-xs font-bold text-ink mb-2">פריטים ({order.items?.length || 0})</h4>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-line text-xs text-ink-muted">
                                            <th className="text-right py-1 font-semibold">מוצר</th>
                                            <th className="text-center py-1 font-semibold w-16">כמות</th>
                                            <th className="text-left py-1 font-semibold w-20">סה״כ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {order.items?.map((item, itemIdx) => {
                                            const quantity = Number(item.quantity) || 1;
                                            const lineTotal = item.total_price != null ? Number(item.total_price) : (Number(item.unit_price) * quantity);
                                            const basePrice = Number(item.unit_price) || 0;
                                            const optionsTotal = (item.paid_options || []).reduce((sum, opt) => sum + (opt.price || 0), 0);
                                            return (
                                                <tr key={itemIdx} className="border-b border-line-subtle">
                                                    <td className="py-1.5 text-ink-strong">
                                                        <div className="font-medium">{item.product_name}</div>
                                                        {item.variant_name && <div className="text-xs text-ink-subtle">{item.variant_name}</div>}
                                                        {item.paid_options?.length > 0 && (
                                                            <div className="text-xs text-ink-muted mt-0.5">
                                                                <span className="font-semibold">תוספות: </span>
                                                                {item.paid_options.map((opt, optIdx) => (
                                                                    <span key={optIdx}>
                                                                        {opt.name} (+₪{opt.price})
                                                                        {optIdx < item.paid_options.length - 1 ? ', ' : ''}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {optionsTotal > 0 && (
                                                            <div className="text-xs text-ink-subtle mt-0.5">
                                                                מחיר בסיס: ₪{basePrice} | תוספות: ₪{optionsTotal} × {quantity}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="py-1.5 text-center text-ink">{quantity}</td>
                                                    <td className="py-1.5 text-left font-medium text-ink-strong">₪{lineTotal % 1 === 0 ? lineTotal : lineTotal.toFixed(2)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {order.notes && (
                                <div className="mt-3 p-2 bg-warning-50 border border-warning-100 rounded text-xs">
                                    <span className="font-semibold">הערות: </span>{order.notes}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/**
 * Orders Summary Table Format - Compact single-table layout so many
 * orders fit on one printed page (one row per order)
 */
const OrdersSummaryTableFormat = ({ orders }) => {
    const orderAreaName = (o) =>
        (o.fulfillment_type === 'delivery_area'
            ? (o.delivery_location_name || o.delivery_area_name)
            : o.delivery_point_name) || '';

    const orderCustomerName = (o) => o.customer?.name || o.customer_name || '';

    // Primary sort by area, secondary by customer name (alphabetical)
    const sortedOrders = useMemo(
        () => [...orders].sort((a, b) => {
            const areaCmp = orderAreaName(a).localeCompare(orderAreaName(b), 'he');
            if (areaCmp !== 0) return areaCmp;
            return orderCustomerName(a).localeCompare(orderCustomerName(b), 'he');
        }),
        [orders]
    );

    const grandTotal = useMemo(
        () => orders.reduce((sum, o) => sum + parseFloat(o.total || o.total_price || 0), 0),
        [orders]
    );

    return (
        <div className="space-y-6 print:space-y-0">
            <div className="text-center mb-8 pb-4 border-b-2 border-mekomit-200 print:hidden">
                <h1 className="text-3xl font-bold text-mekomit-900 mb-2">סיכום הזמנות</h1>
                <p className="text-sm text-ink-muted">
                    {new Date().toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-sm text-ink-muted">סה״כ {orders.length} הזמנות</p>
            </div>

            <table className="w-full print:mt-0 text-xs print:text-[11px]">
                <thead>
                    <tr className="border-b-2 border-mekomit-200">
                        <th className="text-right py-2 px-1 font-bold text-ink-strong">לקוח</th>
                        <th className="text-right py-2 px-1 font-bold text-ink-strong w-24">תאריך</th>
                        <th className="text-right py-2 px-1 font-bold text-ink-strong w-28">טלפון</th>
                        <th className="text-right py-2 px-1 font-bold text-ink-strong w-32">משלוח</th>
                        <th className="text-right py-2 px-1 font-bold text-ink-strong w-24">תשלום</th>
                        <th className="text-right py-2 px-1 font-bold text-ink-strong">פריטים</th>
                        <th className="text-right py-2 px-1 font-bold text-ink-strong">הערות</th>
                        <th className="text-left py-2 px-1 font-bold text-ink-strong w-20">סה״כ</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedOrders.map((order, idx) => {
                        const customerName = order.customer?.name || order.customer_name || 'לא צוין';
                        const customerPhone = order.customer?.phone || order.customer_phone || '';
                        const totalPrice = order.total || order.total_price || '0';
                        const delivery = orderAreaName(order);
                        return (
                            <tr key={order.order_id} className="border-b border-line align-top break-inside-avoid">
                                <td className="py-1.5 px-1 text-ink-strong font-medium">{customerName}</td>
                                <td className="py-1.5 px-1 text-ink-muted whitespace-nowrap">
                                    {new Date(order.created_at).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' })}
                                </td>
                                <td className="py-1.5 px-1 text-ink-muted whitespace-nowrap" dir="ltr">{customerPhone}</td>
                                <td className="py-1.5 px-1 text-ink">
                                    <div>{delivery}</div>
                                    {formatTimeSlot(order) && (
                                        <div className="text-xs text-ink-muted whitespace-nowrap">{formatTimeSlot(order)}</div>
                                    )}
                                </td>
                                <td className="py-1.5 px-1 text-ink">{order.payment_method || ''}</td>
                                <td className="py-1.5 px-1 text-ink">
                                    {order.items?.map((item, itemIdx) => (
                                        <div key={itemIdx} className="py-0.5">
                                            <div>
                                                {(Number(item.quantity) || 1)}× {item.product_name}
                                                {item.variant_name ? ` (${item.variant_name})` : ''}
                                            </div>
                                            {item.paid_options?.length > 0 && (
                                                <div className="text-xs text-ink-muted pr-3">
                                                    + {item.paid_options.map(opt => `${opt.name} (₪${opt.price})`).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </td>
                                <td className="py-1.5 px-1 text-ink-muted whitespace-pre-wrap break-words">{order.notes || ''}</td>
                                <td className="py-1.5 px-1 text-left font-bold text-mekomit-700 whitespace-nowrap">₪{totalPrice}</td>
                            </tr>
                        );
                    })}
                    {orders.length === 0 && (
                        <tr>
                            <td colSpan="8" className="py-8 text-center text-ink-subtle">אין הזמנות להצגה</td>
                        </tr>
                    )}
                </tbody>
                <tfoot>
                    <tr className="border-t-2 border-mekomit-200 bg-mekomit-50">
                        <td colSpan="7" className="py-2 px-1 font-bold text-ink-strong">סה״כ {orders.length} הזמנות</td>
                        <td className="py-2 px-1 text-left font-bold text-mekomit-700 whitespace-nowrap">
                            ₪{grandTotal.toLocaleString('he-IL')}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

/**
 * By Product Format - Summary grouped by product
 */
const ByProductFormat = ({ orders, products = [], categories = [] }) => {
    const NO_CATEGORY = 'ללא קטגוריה';

    const categoryGroups = useMemo(() => {
        // product_id -> category_id, category_id -> name
        const productCategoryId = {};
        (products || []).forEach(p => { productCategoryId[String(p.product_id)] = p.category_id || null; });
        const categoryName = {};
        (categories || []).forEach(c => { categoryName[c.category_id] = c.name; });

        const summary = {};
        orders.forEach(order => {
            order.items?.forEach(item => {
                // Use product_id and variant_id for unique key to avoid issues with whitespace in names
                const productKey = item.product_id + (item.variant_id ? `-${item.variant_id}` : '');
                if (!summary[productKey]) {
                    const catId = productCategoryId[String(item.product_id)];
                    summary[productKey] = {
                        name: (item.product_name || '').trim(),
                        variant: (item.variant_name || '').trim(),
                        category: (catId && categoryName[catId]) ? categoryName[catId] : NO_CATEGORY,
                        totalQuantity: 0,
                        orderIds: new Set()
                    };
                }
                summary[productKey].totalQuantity += Number(item.quantity) || 1;
                summary[productKey].orderIds.add(order.order_id);
            });
        });

        // Group products by category
        const groups = {};
        Object.values(summary).forEach(item => {
            if (!groups[item.category]) groups[item.category] = [];
            groups[item.category].push({
                name: item.name,
                variant: item.variant,
                totalQuantity: item.totalQuantity,
                orderCount: item.orderIds.size
            });
        });

        // Sort categories alphabetically, keeping "no category" last;
        // products within each category sorted by quantity descending
        return Object.keys(groups)
            .sort((a, b) => {
                if (a === NO_CATEGORY) return 1;
                if (b === NO_CATEGORY) return -1;
                return a.localeCompare(b, 'he');
            })
            .map(cat => ({
                category: cat,
                products: groups[cat].sort((a, b) => b.totalQuantity - a.totalQuantity)
            }));
    }, [orders, products, categories]);

    const productSummary = useMemo(
        () => categoryGroups.flatMap(g => g.products),
        [categoryGroups]
    );

    return (
        <div className="space-y-6 print:space-y-0">
            <div className="text-center mb-8 pb-4 border-b-2 border-mekomit-200 print:hidden">
                <h1 className="text-3xl font-bold text-mekomit-900 mb-2">סיכום לפי מוצר</h1>
                <p className="text-sm text-ink-muted">
                    {new Date().toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-sm text-ink-muted">מבוסס על {orders.length} הזמנות</p>
            </div>

            <table className="w-full print:mt-0 print:text-sm">
                <thead>
                    <tr className="border-b-2 border-mekomit-200">
                        <th className="text-right py-3 print:py-2 font-bold text-ink-strong">שם מוצר</th>
                        <th className="text-center py-3 print:py-2 font-bold text-ink-strong w-32">כמות כוללת</th>
                        <th className="text-center py-3 print:py-2 font-bold text-ink-strong w-32">מספר הזמנות</th>
                    </tr>
                </thead>
                <tbody>
                    {categoryGroups.map((group, gIdx) => (
                        <React.Fragment key={gIdx}>
                            <tr className="bg-mekomit-50 break-inside-avoid">
                                <td colSpan="3" className="py-2 px-1 font-bold text-mekomit-800 border-b border-mekomit-200">
                                    {group.category}
                                    <span className="text-xs font-normal text-ink-muted"> · {group.products.reduce((sum, p) => sum + p.totalQuantity, 0)} יח׳</span>
                                </td>
                            </tr>
                            {group.products.map((product, idx) => (
                                <tr key={`${gIdx}-${idx}`} className="border-b border-line">
                                    <td className="py-3 print:py-2 text-ink-strong">
                                        <div className="font-semibold">{product.name}</div>
                                        {product.variant && <div className="text-sm text-ink-muted">{product.variant}</div>}
                                    </td>
                                    <td className="py-3 print:py-2 text-center font-bold text-mekomit-700 text-lg print:text-base">{product.totalQuantity}</td>
                                    <td className="py-3 print:py-2 text-center text-ink">{product.orderCount}</td>
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                    {productSummary.length === 0 && (
                        <tr>
                            <td colSpan="3" className="py-8 text-center text-ink-subtle">אין מוצרים להצגה</td>
                        </tr>
                    )}
                </tbody>
                <tfoot>
                    <tr className="border-t-2 border-mekomit-200 bg-mekomit-50">
                        <td className="py-3 font-bold text-ink-strong">סה״כ</td>
                        <td className="py-3 text-center font-bold text-mekomit-700 text-lg">
                            {productSummary.reduce((sum, p) => sum + p.totalQuantity, 0)}
                        </td>
                        <td className="py-3 text-center font-semibold text-ink">{orders.length}</td>
                    </tr>
                </tfoot>
            </table>

            {categoryGroups.length > 0 && (
                <div className="break-inside-avoid">
                    <h3 className="text-lg font-bold text-ink-strong mb-3 mt-6 print:mt-4">סיכום לפי קטגוריה</h3>
                    <table className="w-full print:text-sm">
                        <thead>
                            <tr className="border-b-2 border-mekomit-200">
                                <th className="text-right py-3 print:py-2 font-bold text-ink-strong">קטגוריה</th>
                                <th className="text-center py-3 print:py-2 font-bold text-ink-strong w-32">מספר מוצרים</th>
                                <th className="text-center py-3 print:py-2 font-bold text-ink-strong w-32">כמות כוללת</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categoryGroups.map((group, idx) => (
                                <tr key={idx} className="border-b border-line">
                                    <td className="py-3 print:py-2 font-semibold text-ink-strong">{group.category}</td>
                                    <td className="py-3 print:py-2 text-center text-ink">{group.products.length}</td>
                                    <td className="py-3 print:py-2 text-center font-bold text-mekomit-700 text-lg print:text-base">
                                        {group.products.reduce((sum, p) => sum + p.totalQuantity, 0)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-mekomit-200 bg-mekomit-50">
                                <td className="py-3 font-bold text-ink-strong">סה״כ</td>
                                <td className="py-3 text-center font-semibold text-ink">
                                    {categoryGroups.reduce((sum, g) => sum + g.products.length, 0)}
                                </td>
                                <td className="py-3 text-center font-bold text-mekomit-700 text-lg">
                                    {categoryGroups.reduce((sum, g) => sum + g.products.reduce((s, p) => s + p.totalQuantity, 0), 0)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
};

/**
 * By Product By Area Format - Product summary grouped by each distinct
 * delivery destination (area / delivery point)
 */
const ByProductByAreaFormat = ({ orders }) => {
    const areaGroups = useMemo(() => {
        const groups = {};

        orders.forEach(order => {
            const areaName =
                (order.fulfillment_type === 'delivery_area'
                    ? order.delivery_location_name || order.delivery_area_name
                    : order.delivery_point_name) ||
                order.delivery_location_name ||
                order.delivery_area_name ||
                order.delivery_point_name ||
                'ללא יעד';

            if (!groups[areaName]) {
                groups[areaName] = { name: areaName, orderIds: new Set(), products: {} };
            }
            const group = groups[areaName];
            group.orderIds.add(order.order_id);

            order.items?.forEach(item => {
                const productKey = item.product_id + (item.variant_id ? `-${item.variant_id}` : '');
                if (!group.products[productKey]) {
                    group.products[productKey] = {
                        name: (item.product_name || '').trim(),
                        variant: (item.variant_name || '').trim(),
                        totalQuantity: 0,
                        orderIds: new Set()
                    };
                }
                group.products[productKey].totalQuantity += Number(item.quantity) || 1;
                group.products[productKey].orderIds.add(order.order_id);
            });
        });

        return Object.values(groups)
            .map(group => ({
                name: group.name,
                orderCount: group.orderIds.size,
                products: Object.values(group.products)
                    .map(p => ({
                        name: p.name,
                        variant: p.variant,
                        totalQuantity: p.totalQuantity,
                        orderCount: p.orderIds.size
                    }))
                    .sort((a, b) => b.totalQuantity - a.totalQuantity)
            }))
            .sort((a, b) => b.orderCount - a.orderCount);
    }, [orders]);

    return (
        <div className="space-y-6 print:space-y-4">
            <div className="text-center mb-8 pb-4 border-b-2 border-mekomit-200 print:hidden">
                <h1 className="text-3xl font-bold text-mekomit-900 mb-2">סיכום מוצרים לפי אזורים</h1>
                <p className="text-sm text-ink-muted">
                    {new Date().toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-sm text-ink-muted">מבוסס על {orders.length} הזמנות · {areaGroups.length} אזורים</p>
            </div>

            <div className="space-y-6 print:space-y-4">
                {areaGroups.map((group, idx) => (
                    <div key={idx} className="break-inside-avoid">
                        <h3 className="text-lg font-bold text-mekomit-900 mb-2 pb-1 border-b-2 border-mekomit-200">
                            {group.name}
                            <span className="text-sm font-normal text-ink-muted"> · {group.orderCount} הזמנות</span>
                        </h3>
                        <table className="w-full print:text-sm">
                            <thead>
                                <tr className="border-b border-mekomit-200">
                                    <th className="text-right py-2 font-bold text-ink-strong">שם מוצר</th>
                                    <th className="text-center py-2 font-bold text-ink-strong w-32">כמות כוללת</th>
                                    <th className="text-center py-2 font-bold text-ink-strong w-32">מספר הזמנות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {group.products.map((product, pIdx) => (
                                    <tr key={pIdx} className="border-b border-line">
                                        <td className="py-2 text-ink-strong">
                                            <div className="font-semibold">{product.name}</div>
                                            {product.variant && <div className="text-sm text-ink-muted">{product.variant}</div>}
                                        </td>
                                        <td className="py-2 text-center font-bold text-mekomit-700 text-lg print:text-base">{product.totalQuantity}</td>
                                        <td className="py-2 text-center text-ink">{product.orderCount}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-mekomit-200 bg-mekomit-50">
                                    <td className="py-2 font-bold text-ink-strong">סה״כ</td>
                                    <td className="py-2 text-center font-bold text-mekomit-700 text-lg">
                                        {group.products.reduce((sum, p) => sum + p.totalQuantity, 0)}
                                    </td>
                                    <td className="py-2 text-center font-semibold text-ink">{group.orderCount}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ))}
                {areaGroups.length === 0 && (
                    <div className="py-8 text-center text-ink-subtle">אין נתונים להצגה</div>
                )}
            </div>
        </div>
    );
};

/**
 * By Delivery Point Format - Summary grouped by delivery point
 */
const ByDeliveryPointFormat = ({ orders, deliveryPoints }) => {
    const deliveryPointSummary = useMemo(() => {
        const summary = {};

        orders.forEach(order => {
            if (order.fulfillment_type !== 'delivery_point' && !order.delivery_point_name) {
                return; // Skip non-delivery-point orders
            }

            const pointName = order.delivery_point_name || 'ללא נקודת חלוקה';
            if (!summary[pointName]) {
                summary[pointName] = {
                    name: pointName,
                    orderCount: 0,
                    productCount: 0,
                    totalRevenue: 0
                };
            }
            summary[pointName].orderCount++;
            summary[pointName].productCount += order.items?.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0) || 0;
            summary[pointName].totalRevenue += parseFloat(order.total || order.total_price || 0);
        });

        return Object.values(summary).sort((a, b) => b.orderCount - a.orderCount);
    }, [orders]);

    return (
        <div className="space-y-6 print:space-y-0">
            <div className="text-center mb-8 pb-4 border-b-2 border-mekomit-200 print:hidden">
                <h1 className="text-3xl font-bold text-mekomit-900 mb-2">סיכום לפי נקודת חלוקה</h1>
                <p className="text-sm text-ink-muted">
                    {new Date().toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-sm text-ink-muted">מבוסס על {orders.length} הזמנות</p>
            </div>

            <table className="w-full print:mt-0 print:text-sm">
                <thead>
                    <tr className="border-b-2 border-mekomit-200">
                        <th className="text-right py-3 font-bold text-ink-strong">נקודת חלוקה</th>
                        <th className="text-center py-3 font-bold text-ink-strong w-28">הזמנות</th>
                        <th className="text-center py-3 font-bold text-ink-strong w-28">פריטים</th>
                        <th className="text-left py-3 font-bold text-ink-strong w-32">הכנסה</th>
                    </tr>
                </thead>
                <tbody>
                    {deliveryPointSummary.map((point, idx) => (
                        <tr key={idx} className="border-b border-line">
                            <td className="py-3 font-semibold text-ink-strong">{point.name}</td>
                            <td className="py-3 text-center text-ink">{point.orderCount}</td>
                            <td className="py-3 text-center text-ink">{point.productCount}</td>
                            <td className="py-3 text-left font-bold text-mekomit-700">₪{point.totalRevenue.toLocaleString('he-IL')}</td>
                        </tr>
                    ))}
                    {deliveryPointSummary.length === 0 && (
                        <tr>
                            <td colSpan="4" className="py-8 text-center text-ink-subtle">אין נקודות חלוקה להצגה</td>
                        </tr>
                    )}
                </tbody>
                <tfoot>
                    <tr className="border-t-2 border-mekomit-200 bg-mekomit-50">
                        <td className="py-3 font-bold text-ink-strong">סה״כ</td>
                        <td className="py-3 text-center font-semibold text-ink">
                            {deliveryPointSummary.reduce((sum, p) => sum + p.orderCount, 0)}
                        </td>
                        <td className="py-3 text-center font-semibold text-ink">
                            {deliveryPointSummary.reduce((sum, p) => sum + p.productCount, 0)}
                        </td>
                        <td className="py-3 text-left font-bold text-mekomit-700">
                            ₪{deliveryPointSummary.reduce((sum, p) => sum + p.totalRevenue, 0).toLocaleString('he-IL')}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

/**
 * By Delivery Area Format - Summary grouped by delivery area
 */
const ByDeliveryAreaFormat = ({ orders, deliveryAreas }) => {
    const deliveryAreaSummary = useMemo(() => {
        const summary = {};

        orders.forEach(order => {
            if (order.fulfillment_type !== 'delivery_area' && !order.delivery_location_name && !order.delivery_area_name) {
                return; // Skip non-delivery-area orders
            }

            const areaName = order.delivery_location_name || order.delivery_area_name || 'ללא אזור משלוח';
            if (!summary[areaName]) {
                summary[areaName] = {
                    name: areaName,
                    orderCount: 0,
                    productCount: 0,
                    totalRevenue: 0
                };
            }
            summary[areaName].orderCount++;
            summary[areaName].productCount += order.items?.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0) || 0;
            summary[areaName].totalRevenue += parseFloat(order.total || order.total_price || 0);
        });

        return Object.values(summary).sort((a, b) => b.orderCount - a.orderCount);
    }, [orders]);

    return (
        <div className="space-y-6 print:space-y-0">
            <div className="text-center mb-8 pb-4 border-b-2 border-mekomit-200 print:hidden">
                <h1 className="text-3xl font-bold text-mekomit-900 mb-2">סיכום לפי אזור משלוח</h1>
                <p className="text-sm text-ink-muted">
                    {new Date().toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-sm text-ink-muted">מבוסס על {orders.length} הזמנות</p>
            </div>

            <table className="w-full print:mt-0 print:text-sm">
                <thead>
                    <tr className="border-b-2 border-mekomit-200">
                        <th className="text-right py-3 font-bold text-ink-strong">אזור משלוח</th>
                        <th className="text-center py-3 font-bold text-ink-strong w-28">הזמנות</th>
                        <th className="text-center py-3 font-bold text-ink-strong w-28">פריטים</th>
                        <th className="text-left py-3 font-bold text-ink-strong w-32">הכנסה</th>
                    </tr>
                </thead>
                <tbody>
                    {deliveryAreaSummary.map((area, idx) => (
                        <tr key={idx} className="border-b border-line">
                            <td className="py-3 font-semibold text-ink-strong">{area.name}</td>
                            <td className="py-3 text-center text-ink">{area.orderCount}</td>
                            <td className="py-3 text-center text-ink">{area.productCount}</td>
                            <td className="py-3 text-left font-bold text-mekomit-700">₪{area.totalRevenue.toLocaleString('he-IL')}</td>
                        </tr>
                    ))}
                    {deliveryAreaSummary.length === 0 && (
                        <tr>
                            <td colSpan="4" className="py-8 text-center text-ink-subtle">אין אזורי משלוח להצגה</td>
                        </tr>
                    )}
                </tbody>
                <tfoot>
                    <tr className="border-t-2 border-mekomit-200 bg-mekomit-50">
                        <td className="py-3 font-bold text-ink-strong">סה״כ</td>
                        <td className="py-3 text-center font-semibold text-ink">
                            {deliveryAreaSummary.reduce((sum, a) => sum + a.orderCount, 0)}
                        </td>
                        <td className="py-3 text-center font-semibold text-ink">
                            {deliveryAreaSummary.reduce((sum, a) => sum + a.productCount, 0)}
                        </td>
                        <td className="py-3 text-left font-bold text-mekomit-700">
                            ₪{deliveryAreaSummary.reduce((sum, a) => sum + a.totalRevenue, 0).toLocaleString('he-IL')}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

/**
 * Revenues Format - Revenue summary with payment method breakdown
 */
const RevenuesFormat = ({ orders }) => {
    const revenueSummary = useMemo(() => {
        const total = orders.reduce((sum, order) => sum + parseFloat(order.total || order.total_price || 0), 0);
        const paymentMethods = {};
        const statusBreakdown = {};

        orders.forEach(order => {
            const amount = parseFloat(order.total || order.total_price || 0);
            const method = order.payment_method || 'לא צוין';
            const status = order.payment_status || 'לא ידוע';

            if (!paymentMethods[method]) {
                paymentMethods[method] = { count: 0, total: 0 };
            }
            paymentMethods[method].count++;
            paymentMethods[method].total += amount;

            if (!statusBreakdown[status]) {
                statusBreakdown[status] = { count: 0, total: 0 };
            }
            statusBreakdown[status].count++;
            statusBreakdown[status].total += amount;
        });

        return {
            total,
            orderCount: orders.length,
            averageOrder: orders.length > 0 ? total / orders.length : 0,
            paymentMethods,
            statusBreakdown
        };
    }, [orders]);

    return (
        <div className="space-y-6 print:space-y-4">
            <div className="text-center mb-8 pb-4 border-b-2 border-mekomit-200 print:hidden">
                <h1 className="text-3xl font-bold text-mekomit-900 mb-2">סיכום הכנסות</h1>
                <p className="text-sm text-ink-muted">
                    {new Date().toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-sm text-ink-muted">מבוסס על {orders.length} הזמנות</p>
            </div>

            {/* Total Revenue Card */}
            <div className="bg-gradient-to-br from-mekomit-50 to-mekomit-100 border-2 border-mekomit-200 rounded-xl p-6 text-center print:mt-0 print:border print:border-line-strong print:rounded-none print:bg-white print:p-4">
                <div className="text-sm font-semibold text-mekomit-600 mb-1">סה״כ הכנסות</div>
                <div className="text-5xl font-bold text-mekomit-900 mb-2">₪{revenueSummary.total.toLocaleString('he-IL')}</div>
                <div className="text-sm text-ink-muted">
                    ממוצע להזמנה: ₪{revenueSummary.averageOrder.toLocaleString('he-IL', { maximumFractionDigits: 2 })}
                </div>
            </div>

            {/* Payment Methods Breakdown */}
            <div>
                <h3 className="text-lg font-bold text-ink-strong mb-3">פירוט לפי אמצעי תשלום</h3>
                <table className="w-full">
                    <thead>
                        <tr className="border-b-2 border-mekomit-200">
                            <th className="text-right py-3 font-bold text-ink-strong">אמצעי תשלום</th>
                            <th className="text-center py-3 font-bold text-ink-strong w-28">הזמנות</th>
                            <th className="text-left py-3 font-bold text-ink-strong w-32">סכום</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(revenueSummary.paymentMethods).map(([method, data], idx) => (
                            <tr key={idx} className="border-b border-line">
                                <td className="py-3 font-semibold text-ink-strong">{method}</td>
                                <td className="py-3 text-center text-ink">{data.count}</td>
                                <td className="py-3 text-left font-bold text-mekomit-700">₪{data.total.toLocaleString('he-IL')}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Payment Status Breakdown */}
            <div>
                <h3 className="text-lg font-bold text-ink-strong mb-3">פירוט לפי סטטוס תשלום</h3>
                <table className="w-full">
                    <thead>
                        <tr className="border-b-2 border-mekomit-200">
                            <th className="text-right py-3 font-bold text-ink-strong">סטטוס</th>
                            <th className="text-center py-3 font-bold text-ink-strong w-28">הזמנות</th>
                            <th className="text-left py-3 font-bold text-ink-strong w-32">סכום</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(revenueSummary.statusBreakdown).map(([status, data], idx) => {
                            const statusLabel = status === 'paid' ? 'שולם' : status === 'pending' ? 'ממתין' : status === 'refunded' ? 'הוחזר' : status;
                            return (
                                <tr key={idx} className="border-b border-line">
                                    <td className="py-3 font-semibold text-ink-strong">{statusLabel}</td>
                                    <td className="py-3 text-center text-ink">{data.count}</td>
                                    <td className="py-3 text-left font-bold text-mekomit-700">₪{data.total.toLocaleString('he-IL')}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
